import React, { useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { XIcon, AlertTriangleIcon, WarehouseIcon, ChevronRightIcon, MapPinIcon } from 'lucide-react';
interface WarehouseAlert {
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  country: string;
  alerts: Array<{
    type: string;
    count: number;
    severity: 'critical' | 'high' | 'medium';
  }>;
}
interface GlobalAlertsModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const mockWarehouseAlerts: WarehouseAlert[] = [{
  warehouseId: 'ir1',
  warehouseCode: 'IR1',
  warehouseName: 'Alkhorayef Manufacture - Riyadh',
  country: 'Saudi Arabia',
  alerts: [{
    type: 'Low Stock',
    count: 15,
    severity: 'critical'
  }, {
    type: 'Order Required',
    count: 8,
    severity: 'critical'
  }]
}, {
  warehouseId: 'is2',
  warehouseCode: 'IS2',
  warehouseName: 'AISco - Albacete',
  country: 'Spain',
  alerts: [{
    type: 'Critical stock with low in transit',
    count: 12,
    severity: 'critical'
  }, {
    type: 'Order Required',
    count: 5,
    severity: 'critical'
  }]
}];
export function GlobalAlertsModal({
  isOpen,
  onClose
}: GlobalAlertsModalProps) {
  const navigate = useNavigate();
  if (!isOpen) return null;
  const handleWarehouseClick = (warehouseId: string) => {
    navigate(`/warehouse/${warehouseId}`);
    onClose();
  };
  const totalAlerts = mockWarehouseAlerts.reduce((sum, warehouse) => sum + warehouse.alerts.reduce((s, alert) => s + alert.count, 0), 0);
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-229">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-230">
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 flex items-center justify-between" data-id="element-231">
          <div className="flex items-center gap-4" data-id="element-232">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center" data-id="element-233">
              <AlertTriangleIcon className="w-6 h-6" data-id="element-234" />
            </div>
            <div data-id="element-235">
              <h2 className="text-2xl font-bold" data-id="element-236">Critical Alerts Overview</h2>
              <p className="text-red-100 text-sm mt-1" data-id="element-237">
                {mockWarehouseAlerts.length} warehouses with critical alerts •{' '}
                {totalAlerts} total issues
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors" data-id="element-238">
            <XIcon className="w-6 h-6" data-id="element-239" />
          </button>
        </div>
        <div className="p-6 border-b border-gray-200 bg-red-50" data-id="element-240">
          <div className="flex items-center gap-6" data-id="element-241">
            <div className="text-center" data-id="element-242">
              <p className="text-sm text-gray-600 mb-1" data-id="element-243">Affected Warehouses</p>
              <p className="text-3xl font-bold text-red-600" data-id="element-244">
                {mockWarehouseAlerts.length}
              </p>
            </div>
            <div className="w-px h-12 bg-gray-300" data-id="element-245"></div>
            <div className="text-center" data-id="element-246">
              <p className="text-sm text-gray-600 mb-1" data-id="element-247">
                Total Critical Alerts
              </p>
              <p className="text-3xl font-bold text-red-600" data-id="element-248">{totalAlerts}</p>
            </div>
            <div className="w-px h-12 bg-gray-300" data-id="element-249"></div>
            <div className="text-center" data-id="element-250">
              <p className="text-sm text-gray-600 mb-1" data-id="element-251">
                Requires Immediate Action
              </p>
              <p className="text-3xl font-bold text-gray-900" data-id="element-252">
                {mockWarehouseAlerts.filter(w => w.alerts.some(a => a.severity === 'critical')).length}
              </p>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6" data-id="element-253">
          <div className="space-y-4" data-id="element-254">
            {mockWarehouseAlerts.map(warehouse => <div key={warehouse.warehouseId} onClick={() => handleWarehouseClick(warehouse.warehouseId)} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-lg hover:border-red-300 transition-all cursor-pointer group" data-id="element-255">
                <div className="flex items-start justify-between mb-4" data-id="element-256">
                  <div className="flex items-start gap-4 flex-1" data-id="element-257">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-lg shadow-red-500/30 group-hover:scale-110 transition-transform" data-id="element-258">
                      <WarehouseIcon className="w-6 h-6 text-white" data-id="element-259" />
                    </div>
                    <div className="flex-1" data-id="element-260">
                      <div className="flex items-center gap-3 mb-2" data-id="element-261">
                        <span className="px-2.5 py-1 bg-gray-100 rounded text-sm font-bold text-gray-700" data-id="element-262">
                          {warehouse.warehouseCode}
                        </span>
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-red-600 transition-colors" data-id="element-263">
                          {warehouse.warehouseName}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-3" data-id="element-264">
                        <MapPinIcon className="w-4 h-4" data-id="element-265" />
                        <span data-id="element-266">{warehouse.country}</span>
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-267">
                        {warehouse.alerts.map((alert, idx) => <div key={idx} className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium border ${getSeverityColor(alert.severity)}`} data-id="element-268">
                            <AlertTriangleIcon className="w-4 h-4" data-id="element-269" />
                            <span data-id="element-270">{alert.type}</span>
                            <span className="font-bold" data-id="element-271">({alert.count})</span>
                          </div>)}
                      </div>
                    </div>
                  </div>
                  <ChevronRightIcon className="w-6 h-6 text-gray-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all flex-shrink-0" data-id="element-272" />
                </div>
                <div className="pt-3 border-t border-gray-100" data-id="element-273">
                  <p className="text-sm text-red-600 font-semibold group-hover:underline" data-id="element-274">
                    Click to view warehouse details and manage alerts →
                  </p>
                </div>
              </div>)}
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 bg-gray-50" data-id="element-275">
          <p className="text-sm text-gray-600 text-center" data-id="element-276">
            Click on any warehouse to view detailed alerts and take action
          </p>
        </div>
      </div>
    </div>;
}